import { useMemo, useState } from "react";

import { ScenarioDecision } from "@/consts/Scenario";
import {
  useReadScenarioApprovalsById,
  useUpdateScenarioApprovals,
} from "@/libs/react-query/hooks/useScenarioApprovalsQuery";
import { showToast } from "@/utils/toastUtils";
import ApprovalAlertDialog from "@/pages/Approval/components/ApprovalAlertDialog";
import { CurrentUserContent } from "@/types/setting";
import useUserStore from "@/stores/User";

type Props = {
  approvalId: string | null;
};

export default function ScenarioApprovalDialog({ approvalId }: Props) {
  const [openAlert, setOpenAlert] = useState(false);
  const [dialogType, setDialogType] = useState<
    ScenarioDecision.APPROVED | ScenarioDecision.REJECTED
  >();

  const storedUser: CurrentUserContent = useUserStore((state) => state.user);

  const { data } = useReadScenarioApprovalsById(approvalId || "");
  const { mutate: submitApproval } = useUpdateScenarioApprovals();

  const onClose = () => {
    setOpenAlert(false);
    setDialogType(undefined);
  };

  const handleSubmit = (reason: string) => {
    if (!approvalId || !dialogType) return;

    submitApproval(
      {
        approval_id: approvalId,
        data: { decision: dialogType, reason },
      },
      {
        onSuccess: () => {
          showToast({
            message: "성공적으로 처리되었습니다.",
          });
        },
      }
    );

    setOpenAlert(false);
  };

  const userApprovalInfo = useMemo(() => {
    if (!data) return null;

    const myStageIndex = data.stages.findIndex((stage) =>
      stage.to_list.some(({ user }) => user.user_id === storedUser.user_id)
    );

    const myStage = data.stages[myStageIndex];
    const myInfo = myStage?.to_list.find(
      ({ user }) => user.user_id === storedUser.user_id
    );

    return {
      isInToList: myInfo !== undefined,
      isMyTurn: data.current_stage - 1 === myStageIndex,
      myDecision: myInfo?.decision ?? ScenarioDecision.UNDECIDED,
    };
  }, [data, storedUser.user_id]);

  const renderApprovalContent = () => {
    if (!data || !userApprovalInfo) return null;
    const { isInToList, isMyTurn, myDecision } = userApprovalInfo;

    const canDecide =
      isInToList && isMyTurn && myDecision === ScenarioDecision.UNDECIDED;

    return (
      <div className="flex items-center gap-2">
        {canDecide ? (
          <>
            <button
              type="button"
              className="py-1 px-2 flex bg-pink text-white text-xs font-semibold rounded-sm"
              onClick={() => {
                setDialogType(ScenarioDecision.REJECTED);
                setOpenAlert(true);
              }}
            >
              반려
            </button>
            <button
              type="button"
              className="py-1 px-2 flex bg-green text-white text-xs font-semibold rounded-sm"
              onClick={() => {
                setDialogType(ScenarioDecision.APPROVED);
                setOpenAlert(true);
              }}
            >
              승인
            </button>
          </>
        ) : null}
      </div>
    );
  };

  return (
    <>
      {renderApprovalContent()}

      <ApprovalAlertDialog
        open={openAlert}
        onClose={onClose}
        onSubmit={handleSubmit}
        type={
          dialogType as ScenarioDecision.APPROVED | ScenarioDecision.REJECTED
        }
      />
    </>
  );
}
